/**
 * Collection Filter Meta Updater
 * Updates page title, H1, and meta description when filters are applied
 */

(function() {
  'use strict';

  /**
   * Extracts active filter labels from URL parameters
   * @returns {Array} Array of filter labels with their values
   */
  function getActiveFiltersFromURL() {
    const params = new URLSearchParams(window.location.search);
    const filters = [];
    
    for (const [key, value] of params) {
      // Filter parameters start with "filter."
      if (key.startsWith('filter.')) {
        // Extract filter label from parameter name
        // Format: filter.v.option.color or filter.v.availability
        const parts = key.split('.');
        
        if (parts.length >= 3) {
          let filterLabel = parts[parts.length - 1];
          
          // Capitalize first letter and convert from camelCase/snake_case
          filterLabel = filterLabel
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .trim()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
          
          // Parse filter values (can be multiple, separated by comma for OR filters)
          const values = value.split(',').map(v => decodeURIComponent(v).trim());
          
          values.forEach(val => {
            // Format value: capitalize and clean up
            const formattedValue = val
              .split(/[-_\s]+/)
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ');
            
            filters.push({
              label: filterLabel,
              value: formattedValue
            });
          });
        }
      }
    }
    
    return filters;
  }

  /**
   * Gets the base collection title from the H1 element or data attribute
   * @returns {string} The base collection title
   */
  function getBaseCollectionTitle() {
    const h1Element = document.querySelector('[data-collection-title]');
    
    if (!h1Element) {
      return 'Collection';
    }
    
    // If filters are already in the title, extract base title
    const currentText = h1Element.textContent.trim();
    const dashIndex = currentText.indexOf(' - ');
    
    if (dashIndex > 0) {
      return currentText.substring(0, dashIndex);
    }
    
    return currentText;
  }

  /**
   * Builds the dynamic title based on collection name and active filters
   * @param {string} baseTitle - The base collection title
   * @param {Array} filters - Array of active filters
   * @param {boolean} includeShopName - Whether to append " - GC-Artis"
   * @returns {string} The formatted title
   */
  function buildDynamicTitle(baseTitle, filters, includeShopName = false) {
    let title = baseTitle;
    
    if (filters.length > 0) {
      const filterParts = filters.map(f => `${f.label} ${f.value}`).join(', ');
      title = `${baseTitle} - ${filterParts}`;
    }
    
    if (includeShopName) {
      title += ' - GC-Artis';
    }
    
    return title;
  }

  /**
   * Builds the dynamic description based on title
   * @param {string} title - The dynamic title without shop name
   * @returns {string} The formatted description
   */
  function buildDynamicDescription(title) {
    return `${title} ➜ Unique designer handmade walking sticks canes. Visit GC-Artis to find your favorite hardwood canes made by skilled artisans`;
  }

  /**
   * Updates the page title, H1, and meta description
   */
  function updatePageMeta() {
    const baseTitle = getBaseCollectionTitle();
    const activeFilters = getActiveFiltersFromURL();
    
    // Update H1
    const h1Element = document.querySelector('[data-collection-title]');
    if (h1Element) {
      const h1Title = buildDynamicTitle(baseTitle, activeFilters, false);
      h1Element.textContent = h1Title;
    }
    
    // Update page title
    const pageTitle = buildDynamicTitle(baseTitle, activeFilters, true);
    document.title = pageTitle;
    
    // Update meta description
    const titleForDescription = buildDynamicTitle(baseTitle, activeFilters, false);
    const description = buildDynamicDescription(titleForDescription);
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);
  }

  /**
   * Observes URL changes (for AJAX filter updates)
   */
  function observeURLChanges() {
    let lastURL = window.location.href;
    
    const observer = new MutationObserver(() => {
      const currentURL = window.location.href;
      if (currentURL !== lastURL) {
        lastURL = currentURL;
        updatePageMeta();
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Initialize the meta updater
   */
  function init() {
    // Only run on collection pages
    if (!document.body.classList.contains('template-collection')) {
      return;
    }
    
    // Initial update
    updatePageMeta();
    
    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', updatePageMeta);
    
    // Listen for custom filter update events (if your theme uses them)
    document.addEventListener('collection:filtered', updatePageMeta);
    
    // Observe URL changes for AJAX updates
    observeURLChanges();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

